import { useState, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { routes } from '../constants/routes';
import {
  loginApi,
  logoutApi,
  googleLoginApi,
  verifyUserApi,
} from '../utils/apis';
import {
  ILoginCredentials,
  IGoogleLoginCredentials,
} from '../schemas/apis/interface';
import { Alert, Snackbar } from '@mui/material';
import { AxiosError } from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { useAppDispatch } from '../hooks/useReduxTypeScript';
import { resetBranding } from '../utils/redux/slices/brandingSlice';
import { resetBillingState } from '../utils/redux/slices/billingSlice';

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

interface ErrorResponse {
  message?: string;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  // Verify authentication status using jwtInterceptor
  const { isLoading: isCheckingAuth } = useQuery({
    queryKey: ['auth', 'verify'],
    queryFn: async () => {
      try {
        await verifyUserApi();
        setIsAuthenticated(true);
        return true;
      } catch (error) {
        console.error('Error verifying user', error);
        setIsAuthenticated(false);
        return false;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: () => {
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      navigate(routes.agents, { replace: true });
      localStorage.removeItem('sidebarScrollPosition');
      localStorage.removeItem('selectedMenus');
      setAlert({
        open: true,
        message: 'Login successful',
        severity: 'success',
      });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      setAlert({
        open: true,
        message: error?.response?.data?.message || 'Login failed',
        severity: 'error',
      });
      throw error;
    },
  });

  // Google login mutation
  const googleLoginMutation = useMutation({
    mutationFn: googleLoginApi,
    onSuccess: () => {
      setIsAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      setAlert({
        open: true,
        message: 'Google login successful',
        severity: 'success',
      });
      navigate(routes.agents, { replace: true });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Google login failed',
        severity: 'error',
      });
      throw error;
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      setIsAuthenticated(false);

      // Reset Redux states
      dispatch(resetBranding());
      dispatch(resetBillingState());

      queryClient.removeQueries({ queryKey: ['agents'] });
      queryClient.removeQueries({ queryKey: ['user'] });
      queryClient.removeQueries({ queryKey: ['configValues'] });
      queryClient.removeQueries({ queryKey: ['templates'] });
      queryClient.removeQueries({ queryKey: ['stores'] });
      queryClient.removeQueries({ queryKey: ['business'] });
      queryClient.removeQueries({ queryKey: ['calls'] });
      queryClient.removeQueries({ queryKey: ['analytics'] });
      queryClient.removeQueries({ queryKey: ['stores'] });
      queryClient.removeQueries({ queryKey: ['brandingLogo'] });

      // Clear menu-related cache specifically
      queryClient.removeQueries({ queryKey: ['menuDataUrl'] });
      queryClient.removeQueries({ queryKey: ['menuData'] });
      queryClient.removeQueries({ queryKey: ['publish-events'] });

      queryClient.clear();
      localStorage.removeItem('selectedAgent');
      localStorage.removeItem('selectedWhatsappAgent');
      localStorage.removeItem('sidebarScrollPosition');
      localStorage.removeItem('selectedMenus');

      // Clear onboarding wizard state so next user doesn't see previous account's progress
      localStorage.removeItem('onboarding_business_data');
      localStorage.removeItem('onboarding_business_step');
      localStorage.removeItem('onboarding_business_completed');
      localStorage.removeItem('onboarding_business_complete');
      localStorage.removeItem('onboarding_agent_data');
      localStorage.removeItem('onboarding_agent_step');
      localStorage.removeItem('onboarding_agent_completed');

      setAlert({
        open: true,
        message: 'Logged out successfully',
        severity: 'success',
      });

      navigate(routes.login, { replace: true, state: {} });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      setAlert({
        open: true,
        message: 'Logout failed',
        severity: 'error',
      });
      throw error;
    },
  });

  const login = async (credentials: ILoginCredentials) => {
    await loginMutation.mutateAsync(credentials);
  };

  const googleLogin = async (credentials: IGoogleLoginCredentials) => {
    await googleLoginMutation.mutateAsync(credentials);
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        googleLogin,
        logout,
        isLoading:
          isCheckingAuth ||
          loginMutation.isPending ||
          googleLoginMutation.isPending ||
          logoutMutation.isPending,
        error:
          loginMutation.error ||
          googleLoginMutation.error ||
          logoutMutation.error ||
          null,
        alert,
        setAlert,
      }}
    >
      {children}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={alert.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};
